import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Info, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Film, Search } from 'lucide-react';
import usePageHeaders from '../hooks/usePageHeaders';

// --- Shared UI Components (Adapted from Articles.js/Home.js) ---

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-4 h-4 bg-black"></div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-black">
      {children}
    </span>
  </div>
);

const Gallery = () => {
  // --- Data State ---
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // --- UI State ---
  const headers = usePageHeaders();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Galeri Produksi - Portofolio Konveksi iMoonusel";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Lihat hasil produksi konveksi kami. Mulai dari seragam kerja, kaos komunitas, hingga merchandise eksklusif dengan kualitas terbaik.");
    setMeta("keywords", "portofolio konveksi, hasil jahit, galeri seragam, contoh kaos, produksi merchandise bandung");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Galeri Produksi - Portofolio Konveksi iMoonusel");
    setOg("og:description", "Dokumentasi visual kualitas produksi kami.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");

    // 2. JSON-LD Schema Markup
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Portofolio iMoonusel Konveksi",
      "description": "Koleksi foto hasil produksi garmen dan konveksi.",
      "url": window.location.href,
      "provider": {
        "@type": "Organization",
        "name": "iMoonusel Konveksi",
        "image": "https://konveksiimoonusel.com/logo512.png"
      }
    };

    const script = document.createElement('script');
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

    // --- Fetch Logic ---

    const fetchGallery = async (pageNum) => {

      setLoading(true);

      try {

        const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/get_gallery.php?page=${pageNum}&limit=16`);

        const newItems = res.data.data;

  

        // Replace items for standard pagination

        setItems(newItems);

        setHasMore(res.data.pagination.has_more);

        setLoading(false);

  

        // Smooth scroll to top of grid

        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {

        console.error("Failed to fetch gallery:", err);

        setLoading(false);

      }

    };

  

    useEffect(() => {

      fetchGallery(page);

    }, [page]);

  // --- Handlers ---
  const isVideo = (url) => url && url.match(/\.(mp4|webm|ogg|mov|avi)$/i);

  const openModal = (index) => {
    setSelectedIndex(index);
    setShowInfo(false);
    setZoomLevel(1);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedIndex(null);
    setShowInfo(false);
    setZoomLevel(1);
    document.body.style.overflow = 'auto';
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (selectedIndex < items.length - 1) {
      setSelectedIndex(prev => prev + 1);
      setZoomLevel(1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      setZoomLevel(1);
    }
  };

  const toggleInfo = (e) => {
    e.stopPropagation();
    setShowInfo(!showInfo);
  };

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') handleNext(e);
      if (e.key === 'ArrowLeft') handlePrev(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex]);

  // --- Render Helpers ---
  const activeItem = selectedIndex !== null ? items[selectedIndex] : null;

  return (
    <div className="min-h-screen bg-white text-black font-sans pt-20">
      
      {/* Editorial Header (Matches Articles.js) */}
      <section className="pt-12 pb-12 border-b border-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6">
            <div>
              <SectionTag>ARCHIVE</SectionTag>
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none mb-2 uppercase">
                Galeri <br/>
                Produksi<span className="text-blue-600">.</span>
              </h1>
            </div>
            <div className="max-w-md text-right md:text-left">
              <p className="text-lg text-zinc-600 font-serif leading-relaxed">
                 Kumpulan foto hasil produksi, proses teknis, dan dokumentasi karya terbaik kami.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Grid Content */}
      <main className="max-w-7xl mx-auto px-6 py-12 md:py-20">
        
        {/* Editorial Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
          {items.map((item, index) => {
            const isVid = isVideo(item.full_url);
            return (
              <div 
                key={`${item.id}-${index}`} 
                className="group relative cursor-pointer block bg-white transition-all duration-300"
                onClick={() => openModal(index)}
              >
                {/* Media Container - Fixed Aspect Ratio for Editorial Look */}
                <div className="relative overflow-hidden w-full bg-zinc-100 border border-zinc-200 aspect-[3/4] mb-3">
                  {isVid ? (
                    <>
                      <video 
                        src={item.full_url} 
                        muted 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-2 right-2 bg-black/80 text-white p-1.5 rounded-none backdrop-blur-sm">
                        <Film size={12} />
                      </div>
                    </>
                  ) : (
                    <img 
                      src={item.full_url} 
                      alt={item.caption_id || "Gallery Item"} 
                      loading="lazy"
                      className="w-full h-full block object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  
                  {/* Subtle border overlay for depth */}
                  <div className="absolute inset-0 border border-black/5 pointer-events-none"></div>
                </div>

                {/* Minimal Caption Below - Matches Home.js Typography */}
                <div className="flex justify-between items-start border-t border-zinc-100 pt-3">
                  <div>
                    {(item.caption_id) ? (
                      <h3 className="text-xs font-bold uppercase tracking-widest text-black group-hover:text-blue-600 transition-colors line-clamp-1">
                        {item.caption_id}
                      </h3>
                    ) : (
                      <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">Untitled</h3>
                    )}
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mt-1 block">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Archive'}
                    </span>
                  </div>
                  
                  {/* Optional Icon/Indicator */}
                  <div className="text-zinc-300 group-hover:text-black transition-colors">
                     <ZoomIn size={14} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-[10px] font-bold uppercase tracking-widest animate-pulse">Loading Records...</div>
          </div>
        )}

        {/* Pagination Buttons */}
        <div className="flex justify-center items-center gap-6 mt-24 border-t border-black pt-8">
            <button 
                disabled={page === 1 || loading} 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-8 py-3 bg-white border border-zinc-200 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-black transition-all"
            >
                Previous
            </button>
            
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Page {page}
            </span>

            <button 
                disabled={!hasMore || loading} 
                onClick={() => setPage(p => p + 1)}
                className="px-8 py-3 bg-white border border-zinc-200 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-black transition-all"
            >
                Next
            </button>
        </div>
      </main>

      {/* --- Fullscreen Editorial Modal (Restyled to match new theme) --- */}
      {activeItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/95 backdrop-blur-xl animate-in fade-in duration-200">
          
          {/* Close Button - Clean & Big */}
          <button 
            onClick={closeModal}
            className="absolute top-6 right-6 z-[110] p-3 text-black hover:text-red-600 transition-colors bg-zinc-100 hover:bg-zinc-200 rounded-none border border-zinc-200"
          >
            <X size={24} />
          </button>

          {/* Navigation - Minimalist Arrows */}
          {items.length > 1 && (
            <>
              <button 
                onClick={handlePrev}
                disabled={selectedIndex === 0}
                className="absolute left-4 md:left-8 z-[105] p-4 text-black hover:bg-black hover:text-white disabled:opacity-0 transition-all border border-zinc-200 bg-white"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                onClick={handleNext}
                disabled={selectedIndex === items.length - 1}
                className="absolute right-4 md:right-8 z-[105] p-4 text-black hover:bg-black hover:text-white disabled:opacity-0 transition-all border border-zinc-200 bg-white"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Main Content Area */}
          <div 
            className="relative w-full h-full flex items-center justify-center p-4 md:p-20 overflow-hidden"
            onClick={closeModal} 
          >
            <div 
              className="relative max-w-full max-h-full transition-transform duration-200 ease-out shadow-2xl"
              onClick={(e) => e.stopPropagation()} 
              style={{ transform: `scale(${zoomLevel})` }}
            >
              {isVideo(activeItem.full_url) ? (
                <video 
                  src={activeItem.full_url} 
                  controls 
                  autoPlay 
                  className="max-w-full max-h-[80vh] object-contain bg-black"
                />
              ) : (
                <img 
                  src={activeItem.full_url} 
                  alt={activeItem.caption_id} 
                  className="max-w-full max-h-[80vh] object-contain select-none bg-white"
                  draggable={false}
                />
              )}
            </div>
          </div>

          {/* Info Toggle Button */}
          <div className="absolute bottom-8 right-8 z-[110] flex gap-4">
             {/* Zoom Controls */}
             <div className="hidden md:flex bg-white border border-zinc-200">
                <button onClick={() => setZoomLevel(z => Math.max(1, z - 0.5))} className="p-3 hover:bg-zinc-100 text-black border-r border-zinc-200"><ZoomOut size={18} /></button>
                <button onClick={() => setZoomLevel(z => Math.min(3, z + 0.5))} className="p-3 hover:bg-zinc-100 text-black"><ZoomIn size={18} /></button>
             </div>

             <button 
               onClick={toggleInfo}
               className={`flex items-center gap-2 px-6 py-3 border transition-all duration-300 font-bold text-xs uppercase tracking-widest ${
                 showInfo 
                   ? 'bg-black text-white border-black' 
                   : 'bg-white text-black border-zinc-200 hover:border-black'
               }`}
             >
               <Info size={16} />
               <span>{showInfo ? 'Close Details' : 'Project Info'}</span>
             </button>
          </div>

          {/* Info Sidebar / Overlay */}
          <div 
            className={`absolute top-0 right-0 h-full w-full md:w-[500px] bg-white border-l border-zinc-200 shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] z-[108] flex flex-col ${
              showInfo ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 overflow-y-auto p-10 scrollbar-thin scrollbar-thumb-zinc-300">
              {/* Header inside Info Panel */}
              <div className="mb-8 border-b border-black pb-6">
                <SectionTag>Project Detail</SectionTag>
                <h2 className="text-3xl font-black text-black leading-tight mb-2 uppercase">
                  {activeItem.caption_id || "Untitled"}
                </h2>
              </div>

              {/* Description */}
              <div className="prose prose-zinc max-w-none text-zinc-600 font-serif leading-loose">
                {activeItem.description_id ? (
                  <div dangerouslySetInnerHTML={{ __html: activeItem.description_id }} />
                ) : (
                  <p className="italic text-zinc-400">No detailed description available.</p>
                )}
              </div>

              {/* Metadata */}
              <div className="mt-12 pt-8 border-t border-zinc-200 grid grid-cols-2 gap-8">
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Uploaded Date</span>
                  <span className="text-sm font-bold text-black">{activeItem.created_at ? new Date(activeItem.created_at).toLocaleDateString() : '—'}</span>
                </div>
                <div>
                   <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Media Type</span>
                   <span className="text-sm font-bold text-black">{isVideo(activeItem.full_url) ? 'Video Asset' : 'Photography'}</span>
                </div>
              </div>
            </div>
            
            {/* Mobile Close Button (inside panel) */}
            <div className="p-6 border-t border-zinc-200 md:hidden">
              <button 
                onClick={() => setShowInfo(false)}
                className="w-full py-4 bg-black text-white font-bold uppercase tracking-widest text-xs"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
